import { Observable, from, mergeMap, toArray, map } from 'rxjs';
import type { GroupingCapKind, GroupingCapWarning } from './grid-orchestration.types';

/** Maximum concurrent grid HTTP requests (row loads, summaries, etc.). */
export const MAX_REQUEST_CONCURRENCY: number = 8;

/** When a high-cardinality field has this many records in one bucket, eager-load its first page. */
export const BUCKET_EAGER_THRESHOLD: number = 10;

/** When the filtered total is at or below this, fetch flat and group client-side. */
export const FLAT_FETCH_THRESHOLD: number = 2000;

/** Number of group headers to render per page (client-side pagination of summary). */
export const GROUP_HEADER_PAGE_SIZE: number = 50;

/** Maximum bucket keys per batch row request. */
export const BATCH_BUCKET_SIZE: number = 20;

/** Row page size per bucket in a batch request (keeps payloads bounded). */
export const BATCH_FIRST_PER_BUCKET: number = 50;

/** Debounce window before flushing a batch of bucket load requests. */
export const BATCH_FLUSH_MS: number = 50;

/**
 * Runs keyed observables with a concurrency limit and returns results as a record.
 *
 * @param entries - Key/observable pairs to execute.
 * @param concurrency - Maximum in-flight observables.
 */
export function forkJoinWithConcurrency<T>(
  entries: Array<[string, Observable<T>]>,
  concurrency: number = MAX_REQUEST_CONCURRENCY
): Observable<Record<string, T>> {
  if (entries.length === 0) {
    return from([{} as Record<string, T>]);
  }
  return from(entries).pipe(
    mergeMap(
      ([key, obs]: [string, Observable<T>]): Observable<{ key: string; result: T }> =>
        obs.pipe(map((result: T): { key: string; result: T } => ({ key, result }))),
      concurrency
    ),
    toArray(),
    map((pairs: Array<{ key: string; result: T }>): Record<string, T> => {
      const out: Record<string, T> = {};
      for (const pair of pairs) {
        out[pair.key] = pair.result;
      }
      return out;
    })
  );
}

/**
 * Normalizes a group summary `value` into the display/bucket key used by the grid.
 *
 * @param value - Raw value from `elementGroupSummary`.
 * @param nullSentinel - Backend sentinel for blank values.
 * @param nullDisplayKey - Frontend display key for blank values.
 */
export function normalizeGroupSummaryKey(
  value: string,
  nullSentinel: string,
  nullDisplayKey: string
): string {
  if (value === nullSentinel) {
    return nullDisplayKey;
  }
  return value;
}

/**
 * Converts a normalized bucket key back to the filter value for `eq` constraints.
 *
 * @param key - Normalized bucket key.
 * @param nullDisplayKey - Frontend display key representing null.
 */
export function bucketKeyToFilterValue(key: string, nullDisplayKey: string): string | null {
  if (key === nullDisplayKey) {
    return null;
  }
  return key;
}

/** Delimiter joining path segments in tree API responses (must match backend). */
export const GROUP_TREE_PATH_DELIMITER: string = '///';

/** Delimiter joining path prefix parts in `_subGroupCounts` keys. */
export const GROUP_TREE_KEY_DELIMITER: string = ':::';

/**
 * Rolls up primary-level bucket counts from multi-segment tree leaf paths.
 *
 * @param nodes - Leaf paths from `elementGroupSummaryTree`.
 * @param nullSentinel - Backend blank sentinel.
 * @param nullDisplayKey - Frontend display key for blank values.
 */
export function buildPrimaryCountsFromTreePaths(
  nodes: ReadonlyArray<{ path: string; count: number }>,
  nullSentinel: string,
  nullDisplayKey: string
): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const node of nodes) {
    const rawSegments: Array<string> = node.path.split(GROUP_TREE_PATH_DELIMITER);
    if (rawSegments.length === 0) {
      continue;
    }

    const primaryKey: string = normalizeGroupSummaryKey(rawSegments[0], nullSentinel, nullDisplayKey);
    counts[primaryKey] = (counts[primaryKey] ?? 0) + node.count;
  }

  return counts;
}

/**
 * Builds `_subGroupCounts` entries by rolling up leaf tree paths to every sub-group level.
 *
 * @param nodes - Leaf paths from `elementGroupSummaryTree`.
 * @param nullSentinel - Backend blank sentinel.
 * @param nullDisplayKey - Frontend display key for blank values.
 */
export function buildSubGroupCountsFromPaths(
  nodes: ReadonlyArray<{ path: string; count: number }>,
  nullSentinel: string,
  nullDisplayKey: string
): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const node of nodes) {
    const rawSegments: Array<string> = node.path.split(GROUP_TREE_PATH_DELIMITER);
    if (rawSegments.length < 2) {
      continue;
    }

    const segments: Array<string> = rawSegments.map((segment: string): string =>
      normalizeGroupSummaryKey(segment, nullSentinel, nullDisplayKey)
    );

    for (let depth: number = 1; depth < segments.length; depth++) {
      const pathPrefix: string = segments.slice(0, depth).join(GROUP_TREE_KEY_DELIMITER);
      const value: string = segments[depth];
      const key: string = `${pathPrefix}${GROUP_TREE_KEY_DELIMITER}${value}`;
      counts[key] = (counts[key] ?? 0) + node.count;
    }
  }

  return counts;
}

/**
 * Computes the maximum number of distinct child keys sharing any single parent prefix
 * in a `_subGroupCounts` map (keys formatted as `parent:::child`).
 */
export function computeMaxDistinctChildrenPerParent(subGroupCounts: Record<string, number>): number {
  const childCountByParent: Record<string, number> = {};

  for (const key of Object.keys(subGroupCounts)) {
    const delimiterIndex: number = key.lastIndexOf(GROUP_TREE_KEY_DELIMITER);
    if (delimiterIndex <= 0) {
      continue;
    }
    const pathPrefix: string = key.slice(0, delimiterIndex);
    childCountByParent[pathPrefix] = (childCountByParent[pathPrefix] ?? 0) + 1;
  }

  let maxDistinctPerParent: number = 0;
  for (const count of Object.values(childCountByParent)) {
    maxDistinctPerParent = Math.max(maxDistinctPerParent, count);
  }
  return maxDistinctPerParent;
}

/**
 * Chunks an array into fixed-size slices (last chunk may be smaller).
 */
export function chunkArray<T>(items: ReadonlyArray<T>, chunkSize: number): Array<Array<T>> {
  if (chunkSize <= 0 || items.length === 0) {
    return new Array<Array<T>>();
  }

  const chunks: Array<Array<T>> = new Array<Array<T>>();
  for (let i: number = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Merges a new cap warning into the current list, retaining one entry per kind and
 * keeping the warning with the higher skipped count when the same kind fires twice.
 */
export function mergeGroupingCapWarnings(
  current: ReadonlyArray<GroupingCapWarning>,
  next: GroupingCapWarning
): Array<GroupingCapWarning> {
  const existingIndex: number = current.findIndex(
    (warning: GroupingCapWarning): boolean => warning.kind === next.kind
  );
  if (existingIndex === -1) {
    return [...current, next];
  }

  const existing: GroupingCapWarning = current[existingIndex];
  if (next.skippedCount >= existing.skippedCount) {
    const updated: Array<GroupingCapWarning> = [...current];
    updated[existingIndex] = next;
    return updated;
  }

  return [...current];
}

/** Transloco key for the user-visible grouping-cap warning message. */
export function groupingCapMessageKey(warning: GroupingCapWarning): string {
  if (warning.kind === 'primaryGroups') {
    return 'components.dataGrid.groupingCapPrimary';
  }
  if (warning.skippedCountUnknown) {
    return 'components.dataGrid.groupingCapDiscoveryTruncated';
  }
  return 'components.dataGrid.groupingCapDiscovery';
}
